import { useCallback, useEffect, useState } from 'react';
import type { Dimension, LevelDescriptor } from '../shared/types.js';
import { api, exportUrl } from './api/client.js';
import { AppShell } from './components/AppShell.js';
import { CaptureView } from './components/CaptureView.js';
import { ReportView } from './components/ReportView.js';
import { FrameworkEditor } from './components/FrameworkEditor.js';

export function App() {
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [descriptors, setDescriptors] = useState<LevelDescriptor[]>([]);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'capture' | 'report' | 'framework'>('capture');

  const [initialEntryId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('entry'),
  );

  useEffect(() => {
    if (initialEntryId) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [initialEntryId]);

  const loadReference = useCallback(() => {
    Promise.all([api.listDimensions(), api.listLevelDescriptors(), api.getConfig()])
      .then(([dims, descs, cfg]) => {
        setDimensions(dims);
        setDescriptors(descs);
        setConfig(cfg);
        setReady(true);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, []);

  useEffect(() => {
    loadReference();
  }, [loadReference]);

  return (
    <AppShell
      view={view}
      onView={setView}
      assigned={config.assigned_level ?? 'T?'}
      target={config.target_level ?? 'T?'}
      exportHref={exportUrl()}
    >
      {error && <p className="error">{error}</p>}
      {ready && view === 'capture' && (
        <CaptureView dimensions={dimensions} descriptors={descriptors} initialEntryId={initialEntryId} />
      )}
      {ready && view === 'report' && <ReportView />}
      {ready && view === 'framework' && (
        <FrameworkEditor
          dimensions={dimensions}
          descriptors={descriptors}
          config={config}
          onChanged={loadReference}
        />
      )}
    </AppShell>
  );
}
